import { useFormik } from 'formik';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { FormikTextInput } from '@coverright/ui/inputs';
import * as yup from "yup";
import * as React from 'react';
import { LoadingButton } from '@mui/lab';
import { useContext, useState } from 'react';
import { ModalCloseButton } from '@coverright/ui/shared';
import { useSaveTagMutation } from '../../shared';
import { TagOutput } from '@coverright/data-access/types/enrollment';
import { Info, Sparkle } from '@phosphor-icons/react';

const validationSchema = yup.object({
  color: yup.string().required('Field is required'),
  name: yup.string().required('Field is required'),
  aiDescription: yup.string().when('aiEnabled', {
    is: true,
    then: schema => schema.required('Required — the AI needs this to know when to apply the tag'),
    otherwise: schema => schema.optional(),
  }),
});

interface FormValues {
  color: string;
  name: string;
  aiEnabled: boolean;
  aiDescription: string;
}

interface Props {
  initialValues?: Partial<FormValues>,
  onSubmit: (values?: FormValues) => void,
  loading: boolean,
}

const AI_TOGGLE_TOOLTIP = 'When enabled, AI will automatically apply or remove this tag based on call transcripts and client interactions — no manual action needed.';
const AI_DESCRIPTION_TOOLTIP = 'This is sent directly to the AI model. Be specific: describe what the tag means, when it should be applied, when it should not, and any edge cases. The more precise you are, the more accurately AI will tag clients.';
const AI_DESCRIPTION_PLACEHOLDER = 'e.g. Apply when the client has clearly expressed intent to enroll within the next 30 days. Do not apply if the client is just exploring options or has mentioned they need more time…';

const Form = ({ initialValues, onSubmit, loading }: Props) => {
  const formik = useFormik<FormValues>({
    initialValues: {
      color: initialValues?.color || '',
      name: initialValues?.name || '',
      aiEnabled: initialValues?.aiEnabled || false,
      aiDescription: initialValues?.aiDescription || '',
    },
    validationSchema,
    onSubmit: values => {
      if (formik.isValid) {
        onSubmit(values);
      }
    },
  });

  const aiEnabled = formik.values.aiEnabled;

  return (
    <Stack component={'form'} gap={2} onSubmit={formik.handleSubmit}>
      <FormikTextInput formik={formik} name={'name'} required />
      <FormikTextInput formik={formik} name={'color'} required />

      {/* AI auto-tagging row */}
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}
             sx={{
               border: '1px solid',
               borderColor: aiEnabled ? '#C4B5FD' : '#E5E7EB',
               borderRadius: '8px',
               px: 1.5,
               py: 1,
               bgcolor: aiEnabled ? '#EDE9FE' : 'transparent',
               transition: 'all 0.2s',
             }}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Sparkle size={15} weight={'fill'} color={aiEnabled ? '#7C3AED' : '#9EA5B1'} />
          <Typography variant={'body2'} fontWeight={500} color={aiEnabled ? '#7C3AED' : 'text.primary'}>
            AI auto-tagging
          </Typography>
          {aiEnabled && (
            <Chip label="Enabled" size="small" sx={{
              height: 18, fontSize: 10, fontWeight: 600,
              bgcolor: '#7C3AED', color: '#fff',
              '& .MuiChip-label': { px: 1 },
            }} />
          )}
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
          <Tooltip title={AI_TOGGLE_TOOLTIP} placement={'left'}>
            <Box sx={{ display: 'flex', cursor: 'help' }}>
              <Info size={15} color={'#6B7280'} />
            </Box>
          </Tooltip>
          <Switch
            size={'small'}
            checked={aiEnabled}
            onChange={e => formik.setFieldValue('aiEnabled', e.target.checked)}
            sx={{
              '& .MuiSwitch-track': { bgcolor: aiEnabled ? '#7C3AED !important' : undefined },
              '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#7C3AED !important' },
            }}
          />
        </Stack>
      </Stack>

      {/* AI description — only when enabled */}
      {aiEnabled && (
        <Stack gap={0.5}>
          <Stack direction={'row'} alignItems={'center'} gap={0.5}>
            <Typography variant={'caption'} fontWeight={600} color={'#7C3AED'}>
              AI description
            </Typography>
            <Typography variant={'caption'} color={'error.main'}>*</Typography>
            <Tooltip title={AI_DESCRIPTION_TOOLTIP} placement={'right'}>
              <Box sx={{ display: 'flex', cursor: 'help' }}>
                <Info size={13} color={'#7C3AED'} />
              </Box>
            </Tooltip>
          </Stack>
          <FormikTextInput
            formik={formik}
            name={'aiDescription'}
            label={''}
            multiline
            rows={4}
            placeholder={AI_DESCRIPTION_PLACEHOLDER}
          />
        </Stack>
      )}

      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Button onClick={() => onSubmit()} variant={'outlined'}>Cancel</Button>
        <LoadingButton loading={loading} type={'submit'} variant={'contained'}>Save</LoadingButton>
      </Stack>
    </Stack>
  );
};

interface ContextProps {
  open: (tag?: TagOutput) => void;
}

const TagFormContext = React.createContext<ContextProps>({ open: () => {} });

export const useTagForm = () => useContext(TagFormContext);

export function withTagForm<P extends object>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [tag, setTag] = useState<TagOutput>();
    const [save, { loading }] = useSaveTagMutation({ refetchQueries: ['tags'] });

    const open = (t?: TagOutput) => {
      setTag(t);
      setDialogOpen(true);
    };

    const handleSubmit = async (values?: FormValues) => {
      if (values) {
        try {
          await save({
            variables: {
              input: {
                id: tag?.id,
                name: values.name,
                color: values.color,
                aiEnabled: values.aiEnabled,
                aiDescription: values.aiEnabled ? values.aiDescription : undefined,
              },
            },
          });
          setDialogOpen(false);
          setTimeout(() => setTag(undefined), 300);
        } catch (error) {}
      }
      setDialogOpen(false);
    };

    return (
      <TagFormContext.Provider value={{ open }}>
        <Dialog fullWidth maxWidth={'xs'} open={dialogOpen} onClose={() => handleSubmit()}>
          <DialogTitle variant={'h3'}>
            {tag ? 'Edit' : 'Create'} tag
            {dialogOpen && <ModalCloseButton onClick={() => handleSubmit()} sx={{ position: 'absolute', top: 4, right: 8 }} />}
          </DialogTitle>
          <DialogContent>
            <Form
              loading={loading}
              initialValues={{
                name: tag?.name || '',
                color: tag?.color || '',
                aiEnabled: tag?.aiEnabled || false,
                aiDescription: tag?.aiDescription || '',
              }}
              onSubmit={handleSubmit}
            />
          </DialogContent>
        </Dialog>
        <WrappedComponent {...props} />
      </TagFormContext.Provider>
    );
  };
}